import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useDispatch } from 'react-redux'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// components
import { TablePagination } from '@mui/material'
// view components
import Risks from './components/RisksContainer'
import LinkingControl from './components/LinkingControl'
import SaveFilterModal from 'components/Modals/SaveFilterModal'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
// constants
import { Modals } from 'constants/modals'
import { RISKS_PER_PAGE } from 'constants/table'
// store
import { setExistingRisk } from 'store/reducers/offlineRisk'
// context
import { RiskViewAPIContext } from 'containers/Risks/RiskView/layers/API'
import { RiskRegisterContext } from 'containers/Risks/RisksRegister/layers'
// themes
import { RiskViewWrapper, TotalCount } from 'components/PageComponents/Risks/Register/components/RiskView/views'
// types
import { IRisk } from 'types/graphql'

const RiskView: React.FC = () => {
  const router = useRouter()
  const dispatch = useDispatch()

  const { showModal } = useShowModal({ modalType: Modals.IMPORTED_RISK_MODAL })

  const { riskView, isScheduled } = React.useContext(RiskRegisterContext)
  const {
    page,
    risks,
    perPage,
    loadMore,
    isLoading,
    deleteRisk,
    totalCount,
    setPerPage,
    onPageChange,
    isInfiniteLoader
  } = React.useContext(RiskViewAPIContext)

  const editRisk = (risk: IRisk) => (openInTab?: boolean) => {
    if (risk.realRiskId && navigator.onLine) {
      showModal({ riskView, risk })
      return
    }

    // The record page derives its hierarchy from the record itself, so the URL carries nothing but
    // the record and its template.
    const pathname = `/forms/edit/${risk.id}/template/${risk.riskTemplate.id}`

    if (!navigator.onLine) {
      dispatch(setExistingRisk(risk))
      router.push(pathname)
      return
    }

    if (openInTab) {
      window.open(pathname, '_blank')
    } else {
      router.push(pathname)
    }
  }

  React.useEffect(() => {
    if (risks && router.query.riskId) {
      const risk = risks.find(
        (item) =>
          item.realRiskId === router.query.riskId &&
          item.fieldValueMap.some((f) => f.fieldValueId === router.query.fieldValueId)
      )

      if (risk) {
        showModal({ riskView, risk })
      }
    }
  }, [router.query, risks?.length])

  return (
    <RiskViewWrapper>
      <LinkingControl />
      <Risks
        risks={risks}
        loadMore={loadMore}
        riskView={riskView}
        editRisk={editRisk}
        isLoading={isLoading}
        totalCount={totalCount}
        deleteRisk={deleteRisk}
        isScheduled={isScheduled}
        isInfiniteLoader={isInfiniteLoader}
        riskTemplateId={riskView.riskTemplate.id}
      />

      {isInfiniteLoader ? (
        <TotalCount>
          {risks.length} of {totalCount}
        </TotalCount>
      ) : (
        <TablePagination
          page={page}
          count={totalCount}
          rowsPerPage={perPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={setPerPage}
          rowsPerPageOptions={RISKS_PER_PAGE}
        />
      )}

      <SaveFilterModal />
      <ConfirmDeleteModal header='Delete Form' modalType={Modals.CONFIRM_DELETE_RISK_MODAL}>
        Are you sure you want to delete this form?
      </ConfirmDeleteModal>
    </RiskViewWrapper>
  )
}

export default RiskView
